/* eslint-disable class-methods-use-this */
import PublicBaseEventRepository from "@app-contexts/public/features/event-detail/data/repositories/PublicBaseEventRepository";

import {
  AuthorizationType,
  type AuthorizationStrategy,
  type AuthorizationMetadata,
  type AuthorizationResult,
} from "../types";

export class TicketTypePasswordStrategy implements AuthorizationStrategy {
  type = AuthorizationType.TICKET_TYPE_PASSWORD;

  static buildAuthorizationKeys(ticketTypeId: string): string[] {
    return [`${AuthorizationType.TICKET_TYPE_PASSWORD}:${ticketTypeId}`];
  }

  async check(
    ticketTypeId: string,
    isAuthorizedFn: (type: AuthorizationType, resourceId: string) => boolean
  ): Promise<AuthorizationResult> {
    // First check if we already have authorization in memory/storage
    if (isAuthorizedFn(AuthorizationType.TICKET_TYPE_PASSWORD, ticketTypeId)) {
      // Already authorized, return empty result (no new authorizations to add)
      return { authorized: true, authorizations: [] };
    }

    // Solely use the authorizedFn instead of using the /check endpoints.
    // For ticket type authorizations, the proper ticket_types are not actually returned from the backend.
    // In other words, TicketType passwords last only throughout the tab session; client-side only.
    // There is currently no way of checking authorization for a specific ticket type.
    // Not authorized, return empty result
    return { authorized: false, authorizations: [] };
  }

  async authorize(
    ticketTypeId: string,
    password: string,
    metadata?: AuthorizationMetadata
  ): Promise<AuthorizationResult> {
    const repo = new PublicBaseEventRepository();
    const response = await repo.checkEventPassword({
      password: password as string,
      slug: (metadata?.eventSlug as string) || "",
      ticket_type: parseInt(ticketTypeId, 10),
    });

    // Ticket type password only authorizes that specific ticket type
    return {
      authorized: true,
      authorizations:
        TicketTypePasswordStrategy.buildAuthorizationKeys(ticketTypeId),
      metadata: {
        password_session_id: response.password_session_id,
      },
    };
  }

  async revoke(ticketTypeId: string): Promise<string[]> {
    return TicketTypePasswordStrategy.buildAuthorizationKeys(ticketTypeId);
  }
}
